/**
 * What went wrong when signing in, and how much of it the person is allowed to be told.
 *
 * plan.md section 8 requires account-enumeration-safe responses, so a wrong password and an address
 * with no account must be indistinguishable. That property lives here, and it is narrower than the
 * rule it replaced. Until now every failure showed the same sentence, which met the requirement by
 * telling everybody the same lie: a captcha refusal, a dropped connection and a 502 all said "Fel
 * e-postadress eller lösenord."
 *
 * Measured on 2026-09-02: the staging deployment answered every browser sign-in with that sentence
 * while the browser had not reached an authentication server at all. The build was pointed at
 * `http://127.0.0.1:54321` and the request never left the machine. A person seeing that message
 * would change a password that was working.
 *
 * So the boundary is this. Anything that is a decision about the credential shares one sentence,
 * because distinguishing those is what enumeration is. Anything that is not a decision about the
 * credential gets its own, because withholding it protects nobody and misdirects the person in
 * front of it. A captcha refusal reveals nothing about whether an account exists. Neither does a
 * timeout, a 500 or a rate limit.
 */

import { isTransportFailure } from "../lib/connection";

export type SignInFailureKind =
  | "credentials"
  | "captcha"
  | "rate_limited"
  | "unavailable"
  | "offline";

interface ErrorShape {
  status?: unknown;
  code?: unknown;
  name?: unknown;
  message?: unknown;
}

const read = (error: unknown): ErrorShape =>
  typeof error === "object" && error !== null ? (error as ErrorShape) : {};

/**
 * The order is the whole design.
 *
 * Captcha is tested before the status buckets because GoTrue returns it as a 400, which is the same
 * status a wrong password uses. Deciding by status first would fold it into the credential message
 * and reproduce exactly the bug this file exists to remove.
 */
export function classifySignInFailure(error: unknown): SignInFailureKind {
  const shape = read(error);
  const status = typeof shape.status === "number" ? shape.status : null;
  const code = typeof shape.code === "string" ? shape.code : "";
  const message = typeof shape.message === "string" ? shape.message : "";

  if (code === "captcha_failed" || /captcha/i.test(message)) {
    return "captcha";
  }

  if (status === 429 || code === "over_request_rate_limit" || code === "over_email_send_rate_limit") {
    return "rate_limited";
  }

  // supabase-js wraps a transport failure as `AuthRetryableFetchError` with status 0, and an
  // unwrapped `fetch` rejection arrives as a TypeError with no status at all. Both mean the request
  // never reached an answer, which is not something the person can fix by retyping anything.
  //
  // The three shapes moved to `app/lib/connection.ts` on 2026-09-08 and this call is what is left of
  // them. They were written here first and stayed here, so every other surface in the portal reported
  // a dropped connection as "Något gick fel. Försök igen." for as long as this file was the only
  // place that knew the difference. One rule, one place, two callers.
  if (isTransportFailure(error)) {
    return "offline";
  }

  if (status !== null && status >= 500) {
    return "unavailable";
  }

  // 400, 401 and 403 are the shapes a credential decision takes. `invalid_credentials`,
  // `email_not_confirmed` and an address with no account all land here and all say the same thing,
  // which is the requirement.
  if (status === 400 || status === 401 || status === 403) {
    return "credentials";
  }

  // Anything left is an answer nobody planned for. It is not reported as a credential decision,
  // because saying "wrong password" about an unexplained fault is how the measured bug happened.
  return status === null ? "offline" : "unavailable";
}

const MESSAGES: Record<SignInFailureKind, string> = {
  credentials: "Fel e-postadress eller lösenord.",
  captcha: "Säkerhetskontrollen gick inte igenom. Ladda om sidan och försök igen.",
  rate_limited: "För många försök. Vänta en stund och försök igen.",
  unavailable: "Något gick fel hos oss. Försök igen om en stund.",
  offline: "Vi når inte servern just nu. Kontrollera uppkopplingen och försök igen."
};

export function signInFailureMessage(kind: SignInFailureKind): string {
  return MESSAGES[kind];
}

/** Every message the sign-in screen can show, for tests that assert the boundary between them. */
export const SIGN_IN_FAILURE_MESSAGES = MESSAGES;
